/**
 * 창 맨 위 크롬 줄.
 * 왼쪽은 열어 둔 플랜 탭이다. 탭을 누르면 그 플랜의 사이드 메뉴로 들어간다.
 * 오른쪽은 공유와 알림이다. 「만들기」는 플랜이 열려 있을 때만 둔다.
 * 워크스페이스 탭에서는 만들지 않는다. 검색은 사이드바 크롬에만 둔다.
 * 계정은 사이드바 아래에 둔다. 화면의 본동사는 제목 줄에 두고, 이 버튼은 만들기 메뉴만 연다.
 */
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip, makeStyles, tokens } from "@fluentui/react-components";
import {
  Board16Regular,
  CheckmarkCircle16Regular,
  ChevronDown12Regular,
  Compose16Regular,
  PeopleTeam16Regular,
  PersonAdd16Regular,
  Share16Regular,
} from "@fluentui/react-icons";
import { type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useSurfaceLocation } from "./settings-url";
import { FuiButton } from "@/components/fui-control";
import { InboxBell } from "@/features/inbox/InboxBell";
import { usePlanSurface } from "@/features/access/use-plan-access";
import { canWorkspace, useWorkspaceRole } from "@/features/access/workspace-access";
import { useCatalogStore } from "@/store/catalog-store";
import { useProjectStore } from "@/store/project-store";
import { chromeHeaderProps } from "./chrome-header";
import { PlanTabBar } from "./PlanTabBar";
import { pathnameOpensPlan, planHref, projectIdFromPath } from "./project-nav";
import { setShareOpen } from "./share-open";

const useStyles = makeStyles({
  bar: {
    display: "flex",
    alignItems: "stretch",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    flexShrink: 0,
    height: "44px",
    minHeight: "44px",
    boxSizing: "border-box",
    padding: `0 ${tokens.spacingHorizontalM} 0 ${tokens.spacingHorizontalS}`,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  right: {
    display: "flex",
    alignItems: "center",
    alignSelf: "center",
    gap: tokens.spacingHorizontalS,
    flex: "0 1 auto",
    minWidth: 0,
    position: "relative",
    zIndex: 2,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  iconBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    padding: 0,
    border: "none",
    borderRadius: tokens.borderRadiusMedium,
    background: "transparent",
    color: tokens.colorNeutralForeground3,
    cursor: "pointer",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground3Hover,
      color: tokens.colorNeutralForeground1,
    },
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "1px",
    },
  },
  divider: {
    width: "1px",
    height: "16px",
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
  /** 회색 크롬 위의 흰 만들기 버튼. 누르면 메뉴만 연다. */
  create: {
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    borderTopColor: tokens.colorNeutralStroke1,
    borderRightColor: tokens.colorNeutralStroke1,
    borderBottomColor: tokens.colorNeutralStroke1,
    borderLeftColor: tokens.colorNeutralStroke1,
    columnGap: "4px",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
      color: tokens.colorNeutralForeground1,
      borderTopColor: tokens.colorNeutralStroke1Hover,
      borderRightColor: tokens.colorNeutralStroke1Hover,
      borderBottomColor: tokens.colorNeutralStroke1Hover,
      borderLeftColor: tokens.colorNeutralStroke1Hover,
    },
    ":hover:active": {
      backgroundColor: tokens.colorNeutralBackground1Pressed,
      color: tokens.colorNeutralForeground1,
    },
  },
});

const createPlan = () => useProjectStore.getState().openDialog("create-plan");

/** 열린 플랜이 없으면 최근 플랜을 연다. 플랜이 하나도 없으면 null. */
function ensurePlan(): string | null {
  const store = useProjectStore.getState();
  if (store.activeId) return store.activeId;
  const latest = Object.values(useCatalogStore.getState().projects).sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))[0];
  if (!latest) return null;
  store.openProject(latest.id);
  return latest.id;
}

/** Task 시트가 이 경로에 마운트되어 있으면 그 자리에서 연다. */
function routeShowsTaskSheet(pathname: string) {
  return pathname === "/home" || pathname === "/tasks" || pathname === "/docs" || pathname === "/files" || pathnameOpensPlan(pathname);
}

function createTask(navigate: (path: string) => void, pathname: string) {
  const id = ensurePlan();
  if (!id) {
    createPlan();
    return;
  }
  useProjectStore.getState().addExecutionTask({}, { open: true });
  if (!routeShowsTaskSheet(pathname)) navigate(planHref(id, "tasks"));
}

function createMenu(canTask: boolean, canPlan: boolean, canInvite: boolean, navigate: (path: string) => void, pathname: string) {
  return [
    ...(canTask ? [{ id: "task", label: "Task", icon: CheckmarkCircle16Regular, onClick: () => createTask(navigate, pathname) }] : []),
    ...(canPlan ? [{ id: "plan", label: "플랜", icon: Board16Regular, onClick: createPlan }] : []),
    ...(canInvite
      ? [
          { id: "team", label: "팀", icon: PeopleTeam16Regular, onClick: () => navigate("/members?view=teams&new=team") },
          { id: "invite", label: "사용자 초대", icon: PersonAdd16Regular, onClick: () => navigate("/members?invite=1") },
        ]
      : []),
  ];
}

export function AppTopBar() {
  const styles = useStyles();
  const navigate = useNavigate();
  const { pathname } = useSurfaceLocation();
  const planId = projectIdFromPath(pathname);
  const planName = useCatalogStore((s) => (planId ? s.projects[planId]?.name : undefined));
  const inPlan = !!planId && !!planName;

  const surface = usePlanSurface();
  const workspaceRole = useWorkspaceRole();
  const canPlan = canWorkspace(workspaceRole, "plan.create");
  const canInvite = canWorkspace(workspaceRole, "member.invite");
  const menu = createMenu(surface.editTask, canPlan, canInvite, navigate, pathname);

  return (
    <div className={styles.bar} {...chromeHeaderProps} data-app-topbar="">
      <PlanTabBar />

      <div className={styles.right}>
        {inPlan && (
          <TopIcon label="공유" onClick={() => setShareOpen(true)}>
            <Share16Regular />
          </TopIcon>
        )}

        <InboxBell />

        {inPlan && menu.length > 0 && <span className={styles.divider} aria-hidden />}

        {inPlan && menu.length > 0 && (
          <Menu positioning="below-end">
            <MenuTrigger disableButtonEnhancement>
              <FuiButton appearance="secondary" className={styles.create} icon={<Compose16Regular />}>
                만들기
                <ChevronDown12Regular />
              </FuiButton>
            </MenuTrigger>
            <MenuPopover>
              <MenuList>
                {menu.map((m) => {
                  const Icon = m.icon;
                  return (
                    <MenuItem key={m.id} icon={<Icon />} onClick={m.onClick}>
                      {m.label}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
      </div>
    </div>
  );
}

function TopIcon({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  const styles = useStyles();
  return (
    <Tooltip content={label} relationship="label" positioning="below">
      <button type="button" className={styles.iconBtn} aria-label={label} onClick={onClick}>
        {children}
      </button>
    </Tooltip>
  );
}
